import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Star } from 'lucide-react';

const MARQUEE_SPEED = 52;
const BUFFER_CARD_COUNT = 2;

const getLoopedItems = (items, startIndex, count) => {
  if (!items.length || !count) return [];

  return Array.from({ length: count }, (_, virtualIndex) => {
    const sequenceIndex = startIndex + virtualIndex;
    const testimonialIndex = sequenceIndex % items.length;

    return {
      key: `testimonial-${sequenceIndex}`,
      testimonial: items[testimonialIndex]
    };
  });
};

const shouldInsetAvatar = (testimonial) => (
  testimonial.company === 'NAPEN'
  || testimonial.company === 'IQ Apartments'
  || testimonial.company === 'RazDva Accounting'
  || testimonial.role.includes('SK Apartments')
);

const renderTestimonialAvatar = (testimonial) => {
  if (testimonial.avatarInitials) {
    return (
      <span
        className="testimonial-avatar-shell testimonial-avatar-shell--initials"
        aria-hidden="true"
      >
        {testimonial.avatarInitials}
      </span>
    );
  }

  return (
    <span className={`testimonial-avatar-shell${shouldInsetAvatar(testimonial) ? ' testimonial-avatar-shell--inset' : ''}`}>
      <img
        src={testimonial.avatar}
        alt=""
        loading="lazy"
        decoding="async"
        className="testimonial-avatar"
      />
    </span>
  );
};

const TestimonialsSection = ({
  testimonials,
  OptimizedSpotlightGroup
}) => {
  const [queueStart, setQueueStart] = useState(0);
  const [renderedItemCount, setRenderedItemCount] = useState(testimonials.length);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const cardPitchRef = useRef(0);
  const offsetRef = useRef(0);
  const frameRef = useRef(null);
  const spotlightFrameRef = useRef(null);
  const spotlightPointerRef = useRef({ x: 0, y: 0 });
  const lastFrameTimeRef = useRef(null);
  const pauseReasonsRef = useRef(new Set());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => {
      setIsReducedMotion(motionQuery.matches);
    };

    updateMotionPreference();

    if (motionQuery.addEventListener) {
      motionQuery.addEventListener('change', updateMotionPreference);
    } else {
      motionQuery.addListener(updateMotionPreference);
    }

    return () => {
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener('change', updateMotionPreference);
      } else {
        motionQuery.removeListener(updateMotionPreference);
      }
    };
  }, []);

  useEffect(() => {
    setQueueStart(0);
    setRenderedItemCount(testimonials.length);
    offsetRef.current = 0;

    if (trackRef.current) {
      trackRef.current.style.transform = isReducedMotion
        ? 'none'
        : 'translate3d(0, 0, 0)';
    }
  }, [isReducedMotion, testimonials.length]);

  useEffect(() => {
    const updateMeasurements = () => {
      const viewport = viewportRef.current;
      const track = trackRef.current;
      const firstCard = track?.querySelector('.testimonial-card');

      if (!viewport || !track || !firstCard || !testimonials.length) return;

      const viewportWidth = viewport.clientWidth;
      const cardWidth = firstCard.getBoundingClientRect().width;
      const trackStyles = window.getComputedStyle(track);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;
      const cardPitch = cardWidth + gap;

      if (!viewportWidth || !cardPitch) return;

      cardPitchRef.current = cardPitch;

      const nextItemCount = Math.max(
        testimonials.length,
        Math.ceil(viewportWidth / cardPitch) + BUFFER_CARD_COUNT
      );

      setRenderedItemCount((currentItemCount) => (
        currentItemCount === nextItemCount ? currentItemCount : nextItemCount
      ));
    };

    updateMeasurements();

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateMeasurements)
      : null;

    if (observer) {
      if (viewportRef.current) {
        observer.observe(viewportRef.current);
      }

      if (trackRef.current) {
        observer.observe(trackRef.current);
      }
    }

    window.addEventListener('resize', updateMeasurements, { passive: true });

    return () => {
      if (observer) {
        observer.disconnect();
      }

      window.removeEventListener('resize', updateMeasurements);
    };
  }, [testimonials.length]);

  useEffect(() => {
    const track = trackRef.current;

    if (!track || !testimonials.length || isReducedMotion) {
      if (track) {
        track.style.transform = 'none';
      }

      return undefined;
    }

    const setTrackOffset = () => {
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
    };

    const recycleVisibleCards = () => {
      const cardPitch = cardPitchRef.current;

      if (!cardPitch) return;

      while (offsetRef.current >= cardPitch) {
        offsetRef.current -= cardPitch;

        flushSync(() => {
          setQueueStart((currentStart) => currentStart + 1);
        });
      }

      setTrackOffset();
    };

    const animate = (frameTime) => {
      const previousFrameTime = lastFrameTimeRef.current;
      lastFrameTimeRef.current = frameTime;

      if (previousFrameTime !== null && pauseReasonsRef.current.size === 0) {
        const elapsedTime = Math.min(frameTime - previousFrameTime, 64);
        offsetRef.current += (elapsedTime / 1000) * MARQUEE_SPEED;
        recycleVisibleCards();
      } else {
        setTrackOffset();
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = null;
      lastFrameTimeRef.current = null;
    };
  }, [isReducedMotion, testimonials.length]);

  useEffect(() => (
    () => {
      if (spotlightFrameRef.current) {
        cancelAnimationFrame(spotlightFrameRef.current);
      }
    }
  ), []);

  const visibleTestimonials = useMemo(
    () => getLoopedItems(testimonials, queueStart, renderedItemCount),
    [queueStart, renderedItemCount, testimonials]
  );

  const pauseMarquee = (reason) => {
    pauseReasonsRef.current.add(reason);
    lastFrameTimeRef.current = null;
  };

  const resumeMarquee = (reason) => {
    pauseReasonsRef.current.delete(reason);
    lastFrameTimeRef.current = null;
  };

  const updateSpotlightPosition = (event) => {
    spotlightPointerRef.current = {
      x: event.clientX,
      y: event.clientY
    };

    if (spotlightFrameRef.current) return;

    spotlightFrameRef.current = requestAnimationFrame(() => {
      const track = trackRef.current;

      if (!track) {
        spotlightFrameRef.current = null;
        return;
      }

      const { x, y } = spotlightPointerRef.current;
      const cards = track.querySelectorAll('.testimonial-card');

      cards.forEach((card) => {
        const cardRect = card.getBoundingClientRect();

        card.style.setProperty('--mouse-x', `${x - cardRect.left}px`);
        card.style.setProperty('--mouse-y', `${y - cardRect.top}px`);
      });

      spotlightFrameRef.current = null;
    });
  };

  const handlePointerEnter = (event) => {
    pauseMarquee('hover');
    updateSpotlightPosition(event);
  };

  const handlePointerLeave = () => {
    resumeMarquee('hover');
  };

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      resumeMarquee('focus');
    }
  };

  const renderTestimonialCard = (testimonial, key) => (
    <article
      key={key}
      className="testimonial-card spotlight-card"
      tabIndex={-1}
    >
      <div className="testimonial-card-content spotlight-content">
        <div className="flex items-center mb-6">
          {renderTestimonialAvatar(testimonial)}
          <div className="min-w-0">
            <h4 className="font-bold truncate">{testimonial.name}</h4>
            <p className="text-slate-400 text-sm truncate">{testimonial.role}</p>
          </div>
        </div>

        <div className="flex mb-4" aria-hidden="true">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" aria-hidden="true" />
          ))}
        </div>

        <p className="testimonial-copy text-slate-300 leading-relaxed">
          "{testimonial.content}"
        </p>
      </div>
    </article>
  );

  const renderAccessibleTestimonials = () => (
    <div className="sr-only">
      <h3>Client testimonials</h3>
      <ul>
        {testimonials.map((testimonial, index) => (
          <li key={`${testimonial.name}-${index}`}>
            <strong>{testimonial.name}</strong>, {testimonial.role}. Rating: {testimonial.rating} out of 5. "{testimonial.content}"
          </li>
        ))}
      </ul>
    </div>
  );

  if (!testimonials.length) {
    return null;
  }

  return (
    <section id="testimonials" className="testimonials-section section-bg-light-glow py-24">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Client Success Stories
            </span>
          </h2>
          <p className="text-xl text-slate-300">
            What our clients say
          </p>
        </div>
      </div>

      {renderAccessibleTestimonials()}

      <div
        ref={viewportRef}
        className="testimonials-marquee-viewport"
        tabIndex={0}
        role="region"
        aria-label="Client testimonials carousel"
        onPointerEnter={handlePointerEnter}
        onPointerMove={updateSpotlightPosition}
        onPointerLeave={handlePointerLeave}
        onFocus={() => pauseMarquee('focus')}
        onBlur={handleBlur}
        onPointerDown={() => pauseMarquee('pointer')}
        onPointerUp={() => resumeMarquee('pointer')}
        onPointerCancel={() => resumeMarquee('pointer')}
      >
        <OptimizedSpotlightGroup>
          <div
            ref={trackRef}
            className="testimonials-marquee-track"
            aria-hidden="true"
          >
            {isReducedMotion
              ? testimonials.map((testimonial, index) => renderTestimonialCard(testimonial, `${testimonial.name}-${index}`))
              : visibleTestimonials.map(({ key, testimonial }) => renderTestimonialCard(testimonial, key))}
          </div>
        </OptimizedSpotlightGroup>
      </div>
    </section>
  );
};

export default TestimonialsSection;
